import { Injectable, Optional } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormlyConfig } from '@ngx-formly/core';
import {
  ICheckDocument,
  IControl,
  IErrorResponse,
  IInput,
} from 'app/services/api5-service/api.interface';
import { Api5Service } from 'app/services/api5-service/api5.service';
import { NotificationService } from 'app/services/notification/notification.service';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  pluck,
  take,
} from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';

import { DisabledService } from './disabled.service';
import { FormService } from './form.service';
import { HiddenService } from './hidden.service';
import { LayoutService } from './layout.service';
import { SetValuesService } from './set-values.service';
import { ISP_VALIDATORS } from './validators';

import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFormState,
  ISPValidator,
} from '../model';
import { findConfig, someParentConfig } from '../utils/formly-configs';
import { isConfigWithControl } from '../utils/is-config-with-control';
import { isFieldValid } from '../utils/is-field-valid';
import { isGeneralBackendError } from '../wrappers/validation-error/validation-error.utils';

const FORM_RENDER_TIME = 100;

/**
 * Loader for validation messages
 */
@UntilDestroy()
@Injectable()
export class ValidationService {
  private readonly patchAfterValidationSubject: Subject<{
    key: string;
    value: string;
  }> = new Subject();

  private readonly generalErrorMsgSubject = new BehaviorSubject<string>('');

  private readonly setValuesGeneralErrorMsgSubject =
    new BehaviorSubject<string>('');

  readonly generalErrorMsg$ = combineLatest([
    this.generalErrorMsgSubject,
    this.setValuesGeneralErrorMsgSubject,
  ]).pipe(
    map(
      ([generalError, setValuesGeneralError]) =>
        setValuesGeneralError || generalError,
    ),
    distinctUntilChanged(),
  );

  constructor(
    private readonly layoutService: LayoutService,
    private readonly formService: FormService,
    private readonly setValuesService: SetValuesService,
    private readonly disabledService: DisabledService,
    private readonly hiddenService: HiddenService,
    private readonly formlyConfig: FormlyConfig,
    private readonly api: Api5Service,
    @Optional() private readonly notificationService?: NotificationService,
  ) {
    this.subscribeToDocChange();
    this.subscribeToSetValuesDoc();
  }

  /**
   * Watch for doc for general error displaying
   */
  private subscribeToDocChange(): void {
    this.formService.doc$
      .pipe(
        // @HACK use delay for properly display backend validation error on field
        // it need because dynamic-form fields asynchronously loaded
        delay(0),
        untilDestroyed(this),
      )
      .subscribe(doc => {
        this.initValidatorMessages(this.formService.formState);

        this.generalErrorMsgSubject.next('');
        if (doc.error) {
          this.handleDocError(doc.error, false);
        }
      });
  }

  /**
   * Watch for set values doc result
   */
  private subscribeToSetValuesDoc(): void {
    this.setValuesService.setValuesRequestEvent$
      .pipe(untilDestroyed(this))
      .subscribe(event => {
        if (event.type === 'end') {
          this.generalErrorMsgSubject.next('');
          if (event.result.setValuesDoc.error && event.showError) {
            this.handleDocError(event.result.setValuesDoc.error, true);
          }
        }
      });
  }

  private addGeneralError(error: IErrorResponse, fromSetValues: boolean): void {
    const errorMessage = DocHelper.getError(error);
    if (!errorMessage) {
      return;
    }

    if (
      this.notificationService &&
      this.formService.formState.showGeneralErrorInNotification
    ) {
      this.notificationService
        .addError$({
          title: this.formService.formState.context.msgError,
          message: errorMessage,
        })
        .subscribe();
    } else {
      if (fromSetValues) {
        this.generalErrorMsgSubject.next(errorMessage);
      } else {
        this.setValuesGeneralErrorMsgSubject.next(errorMessage);
      }
    }
  }

  private addFieldError(error: IErrorResponse): void {
    const errorConfig = findConfig(
      this.formService.configs,
      c =>
        isConfigWithControl(c) &&
        c.props.originalControl.$name === error.$object,
    );
    const errorControl = errorConfig?.formControl;

    if (!errorConfig || !errorControl) {
      return;
    }

    // set timeout for formly form rendering
    setTimeout(() => {
      this.scrollToErrorField(error.$object);
    }, FORM_RENDER_TIME);

    const errorMessage = DocHelper.getError(error) || '';
    this.formlyConfig.addValidatorMessage(
      ISPValidator.ErrorFromBackend,
      () => errorMessage,
    );

    const errorValidator: ValidatorFn = control =>
      ISP_VALIDATORS.errorFromBackend(control, errorConfig);
    // add validator in runtime
    errorControl.addValidators(errorValidator);

    // remove BE errors after user change form model.
    this.formService.formGroup.markAsPristine();

    // update value - trigger appended validator. Validator check form pristine, setted above
    errorControl.markAsTouched({ onlySelf: true });
    errorControl.updateValueAndValidity();

    // watch for all form, because error can be related to some other field. Example - password confirmation
    this.formService.formGroup.valueChanges
      .pipe(
        filter(() => this.formService.formGroup.dirty),
        take(1),
        untilDestroyed(this),
      )
      .subscribe(() => {
        errorControl.updateValueAndValidity();
      });
  }

  handleDocError(error: IErrorResponse, fromSetValues: boolean): void {
    // @HACK when field disabled or hidden we cant show error on it. In this case show error like global
    // but need remove in https://youtrack.ispsystem.net/issue/BILLDr-811
    const fieldName = error.$object;

    const isErrorGeneral = isGeneralBackendError(
      error,
      this.formService.loadedDoc,
    );
    const isErrorFieldHidden =
      fieldName && this.hiddenService.isHidden(fieldName);
    const isErrorFieldDisabled =
      fieldName && this.disabledService.disabledNames.includes(fieldName);

    if (isErrorGeneral || isErrorFieldHidden || isErrorFieldDisabled) {
      this.addGeneralError(error, fromSetValues);
    } else {
      this.addFieldError(error);
    }
  }

  /**
   * BE can send fixed value to apply on form after async validation performing. This is similar to 'setvalues' mechanism
   *
   * @param checkResult - async validation check result
   * @param control - control meta
   * @param formControl - form control
   * @param prevValue - previous value of form control on the moment of validation performing
   */
  applySuggestedValueAfterValidation(
    checkResult: ICheckDocument,
    control: IControl,
    formControl: AbstractControl,
    prevValue: string,
  ): void {
    if (!checkResult.value?.$) {
      // do nothing if BE won't suggest anything
      return;
    }

    if (typeof formControl.value !== 'string') {
      // suggestion applying works only with strings
      return;
    }

    let newValue = checkResult.value.$;
    const isZoomControl = (control as IInput).$zoom;
    // replace the corrected value for the validated value from the field zoom
    // so, BE send back fixed value for every zoom value, and we should replace it in original form control
    if (isZoomControl) {
      const zoomControlValues = formControl.value.split(' ');
      const invalidValueIndex = zoomControlValues.findIndex(
        value => value === prevValue,
      );
      zoomControlValues[invalidValueIndex] = checkResult.value.$;

      newValue = zoomControlValues.join(' ');
    }

    if (newValue !== prevValue) {
      const asyncValidator = formControl.asyncValidator;
      formControl.asyncValidator = null;

      formControl.setValue(newValue, { emitEvent: false });

      formControl.asyncValidator = asyncValidator;
      this.patchAfterValidationSubject.next({
        key: control.$name,
        value: newValue,
      });
    }
  }

  /**
   * Register message for invalid field
   *
   * @param validatorName - validator name
   * @param message - message for invalid validator
   */
  registerValidationMessage(
    validatorName: string,
    message: string | ((error: any, fieldConfig: ISPFieldConfig) => string),
  ): void {
    this.formlyConfig.addValidatorMessage(validatorName, message);
  }

  getNotificationAfterValidationPatch(controlName: string): Observable<string> {
    return this.patchAfterValidationSubject.pipe(
      filter(notify => notify.key === controlName),
      pluck('value'),
    );
  }

  /**
   * Init local validators
   *
   * @param state - form state
   */
  initValidatorMessages(state: ISPFormState): void {
    this.formlyConfig.addValidatorMessage(
      ISPValidator.Required,
      () => state.context.validationRequiredMsg,
    );

    this.formlyConfig.addValidatorMessage(ISPValidator.PassMatch, () =>
      DocHelper.getMessage('msg_pwcheck_donotmatch', state.docSubject.value),
    );

    this.formlyConfig.addValidatorMessage(
      ISPValidator.Duplicate,
      () => state.context.validationDuplicateMsg,
    );

    this.formlyConfig.addValidatorMessage(
      ISPValidator.FileMaxSize,
      (_, fieldConfig: ISPFieldConfig<ISPFieldType.File>) => {
        const errorMsg = state.context.validationFileMaxSizeMsg;
        const formDoc = state.docSubject.value;
        const maxsizeMsg = DocHelper.getMessage(
          `maxsize_${fieldConfig.props.originalControl.$name}`,
          formDoc,
        );
        return errorMsg.replace('__VALUE__', maxsizeMsg);
      },
    );

    const error = DocHelper.getError(state.docSubject.value.error) || '';
    // @TODO maybe redundant because of addFieldError handling
    this.formlyConfig.addValidatorMessage(
      ISPValidator.ErrorFromBackend,
      () => error,
    );
  }

  /**
   * Send request to BE in order to validate value
   *
   * @param control - control metadata
   * @param value - control value
   * @param state - form state
   */
  performAsyncValidation(
    control: IControl,
    value: string,
    state: ISPFormState,
  ): Observable<ICheckDocument> {
    if (!control.$check) {
      return of();
    }
    return this.api.checkFieldValidation({
      func: `check.${control.$check}`,
      name: control.$name,
      funcname: state.docSubject.value.$func,
      value: value,
      args: control.$checkargs,
      tconvert: control.$convert,
    });
  }

  scrollToErrorField(fieldName?: string): void {
    const configToSpot: ISPFieldConfig | undefined = findConfig(
      this.formService.configs,
      config => {
        if (fieldName && isConfigWithControl(config)) {
          return config.props.originalControl.$name === fieldName;
        } else {
          return !isFieldValid(config);
        }
      },
    );

    if (!configToSpot) {
      return;
    }

    const isNeedCheckVisibility = someParentConfig(
      configToSpot,
      field => field.props?.layoutPlace === 'aside',
    );

    this.layoutService
      .scrollToField(configToSpot, isNeedCheckVisibility)
      .then(() => {
        // validation appears on invalid and touched fields. Assumed that this method used on invalid fields
        if (!configToSpot.key || !this.formService.options?.detectChanges) {
          throw new Error('Scroll to field happened before render form!');
        }
        this.formService.formGroup.controls[configToSpot.key].markAsTouched();

        this.formService.options.detectChanges(configToSpot);
      });
  }
}
